import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';

const RecoveryCard = ({ title, description, children }: {
  title: string; description: string; children: React.ReactNode;
}) => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
      <CardFooter><Link to="/auth/login" className="underline">Back to sign in</Link></CardFooter>
    </Card>
  </div>
);

export const ForgotPasswordForm = () => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || sent) return;
    setBusy(true);
    setError('');
    try {
      const result = await resetPassword(email.trim());
      if (result.error) setError(result.error.message);
      else setSent(true);
    } catch {
      setError('Unable to request a reset. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <RecoveryCard title="Reset your password" description="Enter the email address you use for your OG01 account.">
      {sent ? (
        <div role="status" className="space-y-3">
          <p>If an account exists for that email, you will receive a password-reset link.</p>
          <p>Open the link in this same browser to choose your new password.</p>
          <p>If it does not arrive, contact <a className="underline" href="mailto:support@og01.ai">support@og01.ai</a>.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Label htmlFor="recovery-email">Email</Label>
          <Input id="recovery-email" type="email" autoComplete="email" required value={email}
            onChange={event => setEmail(event.target.value)} disabled={busy} />
          {error && <p role="alert" className="text-destructive">{error}</p>}
          <Button type="submit" disabled={busy || !email.trim()} className="w-full">
            {busy ? 'Requesting reset…' : 'Send reset link'}
          </Button>
        </form>
      )}
    </RecoveryCard>
  );
};

export const ResetPasswordForm = () => {
  const [status, setStatus] = useState<'checking' | 'ready' | 'invalid' | 'done'>('checking');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    // The shared client exchanges PKCE codes during initialization. Do not
    // exchange again here: recovery codes can only be consumed once.
    const check = async () => {
      try {
        const query = new URLSearchParams(window.location.search);
        const hash = new URLSearchParams(window.location.hash.slice(1));
        if (query.has('error') || query.has('error_code') || hash.has('error') || hash.has('error_code')) {
          if (active) setStatus('invalid');
          return;
        }
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (!active) return;
        if (sessionError || !data.session) {
          setStatus('invalid');
          return;
        }
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (active) setStatus(!userError && userData.user ? 'ready' : 'invalid');
      } catch {
        if (active) setStatus('invalid');
      }
    };
    void check();
    return () => { active = false; };
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || status !== 'ready') return;
    if (password.length < 8 || password !== confirmation) {
      setError('Use at least 8 characters and enter the same password twice.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      setPassword('');
      setConfirmation('');
      setStatus('done');
    } catch {
      setError('Unable to update your password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <RecoveryCard title="Choose a new password" description="Set a new password for your OG01 account.">
      {status === 'checking' && <p role="status">Checking your reset link…</p>}
      {status === 'invalid' && <div role="alert" className="space-y-3">
        <p>This reset link is invalid or expired. Request a new link and open it in the browser where you requested it.</p>
        <Link className="underline" to="/auth/forgot-password">Request a new reset link</Link>
      </div>}
      {status === 'done' && <div role="status" className="space-y-3">
        <p>Your password has been updated.</p>
        <Link className="underline" to="/ruby-aissist">Open Managed Improvement</Link>
      </div>}
      {status === 'ready' && <form onSubmit={submit} className="space-y-4">
        <Label htmlFor="new-password">New password</Label>
        <Input id="new-password" type="password" autoComplete="new-password" minLength={8} required
          value={password} onChange={event => setPassword(event.target.value)} disabled={busy} />
        <Label htmlFor="confirm-password">Confirm new password</Label>
        <Input id="confirm-password" type="password" autoComplete="new-password" minLength={8} required
          value={confirmation} onChange={event => setConfirmation(event.target.value)} disabled={busy} />
        {error && <p role="alert" className="text-destructive">{error}</p>}
        <Button type="submit" disabled={busy || !password || !confirmation} className="w-full">
          {busy ? 'Updating password…' : 'Update password'}
        </Button>
      </form>}
    </RecoveryCard>
  );
};
